/*  ========================================================================
    # Posts
    ========================================================================  */

import React, { useEffect, useState } from 'react';
import type { PostsProps } from '.';
import bem from '@/react/utils/bem';

// Hooks
import useBreakpoints from '@/react/hooks/useBreakpoints';
import useUrlParams from '@/react/hooks/useUrlParams';
import useTranslation from '@/react/hooks/useTranslation';
import useLocalization from '@/react/hooks/useLocalization';
import usePostsApi from '@/react/hooks/usePostsApi';

// Components
import { PostCard, PostCardSkeleton } from '@/react/components/PostCard';
import Pagination from '@/react/components/Pagination';

const Posts = ({
  headline,
  displayType,
  categories,
  ctaText,
  ctaLink,
  predefinedPosts,
  showCategoryPagination,
  preSelectedCategory,
  link,
  excludedCategories
}: PostsProps) => {
  const { md, xl } = useBreakpoints();
  const [postsPerPage, setPostsPerPage] = useState(() => {
    if (displayType === 'newest' || displayType === 'single_category') {
      return 3;
    } else if (xl) {
      return 6;
    } else if (md) {
      return 4;
    } else {
      return 2;
    }
  });
  const { params: urlParams, setUrlParams } = useUrlParams();
  const currentPage = parseInt(urlParams.get('currentPage') || '1', 10);
  let selectedCategoriesParam: string[] | number[] = urlParams.get('categories')?.split(',') || [];
  selectedCategoriesParam = [...selectedCategoriesParam.map(Number)];
  const setSelectedCategoriesParam = (categories: number[]) => {
    setUrlParams({ categories: categories.join(',') });
  };
  const [selectedCategories, setSelectedCategories] = useState(
    displayType === 'single_category' ? preSelectedCategory : selectedCategoriesParam
  );
  const searchValue = urlParams.get('search') || '';
  const [searchInput, setSearchInput] = useState(searchValue);
  const handlePagination = (page: number) => setUrlParams({ currentPage: page });

  // Hooks
  const localize = useLocalization();
  const __ = useTranslation();

  /**
   * Use WordPress Posts REST API.
   */
  const { loadingPosts, postsCount, posts } = usePostsApi({
    per_page: postsPerPage,
    page: currentPage,
    categories: selectedCategories,
    search: searchValue,
    categories_exclude: excludedCategories
  });

  /**
   * Handle selecting category.
   */
  const selectCategory = (categoryID: number) => {
    if (selectedCategories.includes(categoryID)) {
      const tempCategories = selectedCategories;
      tempCategories.splice(tempCategories.indexOf(categoryID), 1);
      if (tempCategories.length > 0) {
        setSelectedCategories([...tempCategories]);
        setSelectedCategoriesParam([...tempCategories]);
      }
      if (tempCategories.length === 0) {
        setSelectedCategories([]);
        setSelectedCategoriesParam([]);
      }
    } else {
      setSelectedCategories(prev => [...prev, categoryID]);
      setSelectedCategoriesParam([...selectedCategories, categoryID]);
    }
  };

  /**
   * Check how many 'postsPerPage' are needed for the current breakpoint
   * and set current page to 1 on window resize.
   */
  useEffect(() => {
    window.addEventListener(
      'resize',
      () => {
        if (showCategoryPagination || displayType === 'all_posts') {
          if (xl) {
            setPostsPerPage(6);
          } else if (md) {
            setPostsPerPage(4);
          } else {
            setPostsPerPage(2);
          }

          setUrlParams({ currentPage: 1 });
        }
      },
      { passive: true }
    );
  }, []);

  return (
    <div className='container'>
      {headline ? (
        <div
          className='s-posts__headline wysiwyg-block'
          dangerouslySetInnerHTML={{ __html: headline }}
        />
      ) : null}

      {/* Filter Bar */}
      {displayType === 'all_posts' ? (
        <div className='s-posts__filter-bar'>
          {categories.length > 0 ? (
            <div className={`s-posts__categories`}>
              {categories.map((filter, index) => (
                <button
                  key={index}
                  className={bem('s-posts', 'category', {
                    active: selectedCategories.includes(filter.cat_ID)
                  })}
                  onClick={() => selectCategory(filter.cat_ID)}
                >
                  {filter.name}
                </button>
              ))}
            </div>
          ) : null}
          <form className='s-posts__search' onSubmit={event => event.preventDefault()}>
            <label htmlFor='section-posts-search-input'>
              <input
                id='section-posts-search-input'
                type='search'
                placeholder={__('section-posts-search-input')}
                value={searchInput}
                onChange={event => setSearchInput(event.target.value)}
              />
            </label>
            <div
              className={bem('s-posts', 'search-clear', { active: searchInput !== '' })}
              onClick={() => {
                setUrlParams({ currentPage: null, search: null });
                setSearchInput('');
              }}
            >
              <span />
              <span />
            </div>
            <button
              type='submit'
              onClick={() => setUrlParams({ currentPage: null, search: searchInput })}
            >
              <svg viewBox='0 0 24 24'>
                <use href={`${localize('svgSprite')}#search`} />
              </svg>
            </button>
          </form>
        </div>
      ) : null}

      {/* All Posts or Newest */}
      {displayType === 'all_posts' ||
        displayType === 'newest' ||
        displayType === 'single_category' ? (
        <>
          <div className='s-posts__grid'>
            {loadingPosts ? (
              <>
                {[...Array(postsPerPage)].map((post, index) => (
                  <PostCardSkeleton key={`${index}-skeleton`} />
                ))}
              </>
            ) : null}
            {!loadingPosts && posts.length > 0 ? (
              <>
                {posts?.map((post, index) => (
                  <PostCard
                    key={`${index}-${post.title}`}
                    permalink={post.permalink}
                    category={post.category}
                    date={post.date}
                    title={post.title}
                    excerpt={post.excerpt}
                    image={post.image}
                  />
                ))}
              </>
            ) : null}
          </div>
          {!loadingPosts && posts.length === 0 ? (
            <h3 className='tw-mb-16 tw-text-center'> {__('section-posts-no-posts-found')}</h3>
          ) : null}
        </>
      ) : null}

      {/* Predefined Posts */}
      {displayType !== 'all_posts' ? (
        <div className='s-posts__grid'>
          {predefinedPosts?.map((post, index) => (
            <PostCard
              key={`${index}-${post.title}`}
              permalink={post.permalink}
              category={post.category}
              date={post.date}
              title={post.title}
              excerpt={post.excerpt}
              image={post.image}
            />
          ))}
        </div>
      ) : null}

      {/* CTA */}
      {displayType === 'all_posts' && ctaText !== '' && ctaLink !== null ? (
        <div className='s-posts__cta'>
          <div
            className='s-posts__cta-content tw-w-full xl:tw-w-9/12'
            dangerouslySetInnerHTML={{ __html: ctaText }}
          />
          <div className='s-posts__cta-link tw-w-full xl:tw-w-3/12'>
            <a href={ctaLink.url} className='c-button c-button--append c-button--white-hover'>
              {ctaLink.title}
            </a>
          </div>
        </div>
      ) : null}

      {/* Pagination */}
      {displayType === 'all_posts' ? (
        <Pagination
          itemsCount={postsCount}
          itemsPerPage={postsPerPage}
          currentPage={currentPage}
          handlePagination={handlePagination}
        />
      ) : null}

      {/* Load More Button */}
      {displayType === 'single_category' && showCategoryPagination ? (
        <Pagination
          itemsCount={postsCount}
          itemsPerPage={postsPerPage}
          currentPage={currentPage}
          handlePagination={handlePagination}
        />
      ) : null}

      {/* Link */}
      {displayType !== 'all_posts' && link !== null && link.length > 0 ? (
        <div className='s-posts__link'>
          <a className='c-button' href={link.url}>
            {link.title}
          </a>
        </div>
      ) : null}
    </div>
  );
};

export default Posts;
